/**
 * G16: Polygon Merge Utility
 * Edge-based merge algorithm for adjacent cadastral parcels.
 * ULDK parcels sharing an edge have exact matching vertices,
 * so we can use simple edge removal + ring walking.
 */

import type { PL2000Coordinate, ParcelResult } from './types'
import { coordinateService } from './CoordinateService'

/** Snap tolerance in meters (0.01m = 1cm) */
const SNAP_TOLERANCE = 0.01

/**
 * Snap a coordinate value to the tolerance grid
 */
function snap(v: number): string {
  return (Math.round(v / SNAP_TOLERANCE) * SNAP_TOLERANCE).toFixed(2)
}

/**
 * Create a directed edge key from two PL-2000 coordinates
 */
function edgeKey(a: PL2000Coordinate, b: PL2000Coordinate): string {
  return `${snap(a.x)},${snap(a.y)}->${snap(b.x)},${snap(b.y)}`
}

/**
 * Create a node key from a PL-2000 coordinate (snapped)
 */
function nodeKey(c: PL2000Coordinate): string {
  return `${snap(c.x)},${snap(c.y)}`
}

/**
 * Merge adjacent polygons by removing shared edges and walking the remaining boundary.
 *
 * Algorithm:
 * 1. Collect all directed edges from all polygons
 * 2. Remove pairs of opposite edges (shared between adjacent parcels)
 * 3. Walk remaining edges to form closed ring(s)
 *
 * @returns Array of rings — 1 if all polygons are adjacent, multiple if disjoint groups exist
 */
export function mergeAdjacentPolygons(polygons: PL2000Coordinate[][]): PL2000Coordinate[][] {
  if (polygons.length === 0) return []
  if (polygons.length === 1) return [polygons[0]]

  // Step 1: Collect all directed edges, counting occurrences
  // Key = directed edge, Value = { count, from, to }
  const edgeCounts = new Map<string, { count: number; from: PL2000Coordinate; to: PL2000Coordinate }>()

  for (const polygon of polygons) {
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i]
      const b = polygon[(i + 1) % polygon.length]
      const key = edgeKey(a, b)
      const existing = edgeCounts.get(key)
      if (existing) {
        existing.count++
      } else {
        edgeCounts.set(key, { count: 1, from: a, to: b })
      }
    }
  }

  // Step 2: Remove shared edges — an edge is shared if its reverse also exists
  const remainingEdges: Array<{ from: PL2000Coordinate; to: PL2000Coordinate }> = []

  for (const [, edge] of edgeCounts) {
    // Build reverse key
    const reverseKey = edgeKey(edge.to, edge.from)
    if (edgeCounts.has(reverseKey)) {
      // Shared edge — skip both directions
      continue
    }
    // Keep this edge (may appear multiple times if overlapping polygons, but that shouldn't happen with parcels)
    for (let i = 0; i < edge.count; i++) {
      remainingEdges.push({ from: edge.from, to: edge.to })
    }
  }

  if (remainingEdges.length === 0) return []

  // Step 3: Build adjacency map (node -> list of outgoing edges)
  const adjacency = new Map<string, Array<{ to: PL2000Coordinate; toKey: string; used: boolean }>>()

  for (const edge of remainingEdges) {
    const fromKey = nodeKey(edge.from)
    if (!adjacency.has(fromKey)) {
      adjacency.set(fromKey, [])
    }
    adjacency.get(fromKey)!.push({
      to: edge.to,
      toKey: nodeKey(edge.to),
      used: false
    })
  }

  // Step 4: Walk edges to form closed rings
  const rings: PL2000Coordinate[][] = []

  for (const [startKey, edges] of adjacency) {
    for (const startEdge of edges) {
      if (startEdge.used) continue

      // Start a new ring
      const ring: PL2000Coordinate[] = []
      let currentKey = startKey
      let nextEdge: typeof startEdge | undefined = startEdge

      while (nextEdge && !nextEdge.used) {
        nextEdge.used = true
        ring.push(nextEdge.to)

        currentKey = nextEdge.toKey
        if (currentKey === startKey) break // Ring closed

        // Find next unused outgoing edge
        const outgoing = adjacency.get(currentKey)
        nextEdge = outgoing?.find(e => !e.used)
      }

      if (ring.length >= 3) {
        rings.push(ring)
      }
    }
  }

  return rings
}

/**
 * Check if merging all polygons produces exactly 1 polygon (i.e., all are adjacent)
 */
export function areAllAdjacent(polygons: PL2000Coordinate[][]): boolean {
  if (polygons.length <= 1) return true
  const merged = mergeAdjacentPolygons(polygons)
  return merged.length === 1
}

/**
 * High-level wrapper: merge multiple ParcelResults into one.
 *
 * - Merges PL-2000 boundaries using edge removal
 * - Takes the largest polygon if non-adjacent (disjoint groups)
 * - Converts back to WGS84
 * - Returns synthetic ParcelResult with composite ID and summed area
 */
export function mergeParcels(parcels: ParcelResult[]): ParcelResult {
  if (parcels.length === 0) throw new Error('No parcels to merge')
  if (parcels.length === 1) return parcels[0]

  // Merge PL-2000 boundaries
  const pl2000Polygons = parcels.map(p => p.boundaryPl2000)
  const mergedRings = mergeAdjacentPolygons(pl2000Polygons)

  // Pick the boundary to use
  let mergedBoundaryPl2000: PL2000Coordinate[]
  if (mergedRings.length === 1) {
    mergedBoundaryPl2000 = mergedRings[0]
  } else if (mergedRings.length > 1) {
    // Take the largest ring by vertex count (approximation for area)
    mergedBoundaryPl2000 = mergedRings.reduce((largest, ring) =>
      ring.length > largest.length ? ring : largest
    )
  } else {
    // Fallback: shouldn't happen, use first parcel
    mergedBoundaryPl2000 = parcels[0].boundaryPl2000
  }

  // Convert to WGS84
  const mergedBoundary = coordinateService.pl2000ArrayToWgs84(mergedBoundaryPl2000)

  // Composite ID and summed area
  const compositeId = parcels.map(p => p.id).join('+')
  const totalArea = parcels.reduce((sum, p) => sum + p.area, 0)

  // Use region from first parcel (they should be in the same area)
  const region = parcels[0].region

  return {
    id: compositeId,
    boundary: mergedBoundary,
    boundaryPl2000: mergedBoundaryPl2000,
    region,
    area: totalArea,
    address: parcels[0].address,
  }
}
